import React from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { koKR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/ko';

const Container = styled(Box)({});

const ContentWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    padding: '16px',
    paddingRight: '10px'
});

const InputWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
});

const InputField = styled(TextField)({
    backgroundColor: '#fff',
    shrink: false,
    width: '170px',
    '& .MuiOutlinedInput-root': {
        height: '36px',
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 14px',
    },
});

const RightButton = styled(Button)(({ rightBtnbgcolor }) => ({
    backgroundColor: rightBtnbgcolor,
    boxShadow: 'none',
    height: '36px',
    width: '130px',
    marginLeft: '16px',
    color: '#fff',
    '&:hover': { backgroundColor: rightBtnbgcolor },
    '&:disabled': {
        backgroundColor: 'var(--sub-mediumgrey-color)',
        color: 'grey',
        cursor: 'not-allowed',
    },
}));


const CustomStyledButton = styled(Button)({
    boxShadow: 'none',
    height: '36px',
    textTransform: 'none',
    padding: '0 40px',
    backgroundColor: 'var(--main-blue-color)',
    '&:hover': { backgroundColor: 'var(--main-blue-color)' },
    color: 'var(--main-white-color)'
});

// 검색 필드 종류 지원
const FieldComponent = ({ type, value, onChange, options = [], error, ...props }) => {
    switch (type) {
        case 'select':
            const defaultValue = options.length > 0 ? options[0].value : '';
            return (
                <FormControl sx={{ width: '170px', position: 'relative' }} error={!!error}>
                    <Select
                        value={value || defaultValue}
                        onChange={onChange}
                        sx={{ height: '36px', bgcolor: '#fff' }}
                    >
                        {options.map((option, index) => (
                            <MenuItem key={index} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {error && (
                        <Typography 
                            color="error" 
                            variant="caption" 
                            sx={{ 
                                position: 'absolute',
                                bottom: '-20px',
                                left: 0
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </FormControl>
            );
        case 'calendar':
            return (
                <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDayjs} 
                        adapterLocale="ko"
                        localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
                    >
                        <DatePicker
                            value={value ? dayjs(value) : null}
                            onChange={(newValue) => {
                                onChange({
                                    target: {
                                        value: newValue ? newValue.format('YYYY-MM-DD') : ''
                                    }
                                });
                            }}
                            format="YYYY-MM-DD"
                            minDate={dayjs('2020-01-01')}
                            maxDate={dayjs()}
                            sx={{
                                '& .MuiCalendarPicker-root': { 
                                    width: '240px',
                                    height: '260px'
                                },
                                '& .MuiPickersDay-root': { 
                                    fontSize: '0.75rem',
                                    width: '28px',
                                    height: '28px'
                                },
                                '& .MuiDayPicker-header': {
                                    '& .MuiTypography-root': {
                                        fontSize: '0.75rem'
                                    }
                                },
                                '& .MuiPickersCalendarHeader-root': {
                                    paddingTop: '8px',
                                    paddingBottom: '8px'
                                }
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    error: !!error,
                                    sx: {
                                        backgroundColor: '#fff',
                                        width: '170px',
                                        '& .MuiOutlinedInput-root': {
                                            height: '36px',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px 14px',
                                        }
                                    }
                                },
                                actionBar: {
                                    actions: ['today']
                                }
                            }}
                        />
                    </LocalizationProvider>
                    {error && (
                        <Typography 
                            color="error" 
                            variant="caption" 
                            sx={{ 
                                position: 'absolute',
                                bottom: '-20px',
                                left: 0
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>
            );
        case 'input':
        default:
            return (
                <Box sx={{ position: 'relative' }}>
                    <InputField
                        variant="outlined"
                        size="small"
                        value={value || ''}
                        autoComplete="off"
                        onChange={onChange}
                        error={!!error}
                        {...props}
                    />
                    {error && (
                        <Typography 
                            color="error" 
                            variant="caption" 
                            sx={{ 
                                position: 'absolute',
                                bottom: '-20px',
                                left: 0
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>
            );
    }
};

const SearchArea = ({
    searchValues,
    onSearchClick,
    notSearch,
    handleSearchChanges,
    showRightButton,
    onRightBtnClick,
    rightBtnText,
    rightBtnbgcolor,
    rightBtnDisabled,
    fields = [],
    errors = {}
}) => {
    return (
        <Container>
            <ContentWrapper>
                <InputWrapper>
                    {fields.map((field, index) => (
                        <Box key={index} display="flex" alignItems="center" sx={{ mr: 2.5 }}>
                            <Typography sx={{ marginRight: 1 }}>{field.fieldnm}</Typography>
                            <FieldComponent
                                type={field.type}
                                value={searchValues[field.name]}
                                onChange={(e) => handleSearchChanges(field.name, e.target.value)}
                                error={errors[field.name]}
                                {...field}
                            />
                        </Box>
                    ))}
                    {notSearch ? (null) :
                        (
                            <CustomStyledButton onClick={onSearchClick}>검색</CustomStyledButton>
                        )
                    }
                </InputWrapper>
                {showRightButton && (
                    <RightButton 
                        onClick={onRightBtnClick} 
                        rightBtnbgcolor={rightBtnbgcolor}
                        disabled={rightBtnDisabled}
                    >
                        {rightBtnText}
                    </RightButton>
                )}
            </ContentWrapper>
        </Container>
    );
};

export default SearchArea;
